import { useState, useMemo, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CIF } from "@/constants/testIds";
import { Activity, Package, MapPin, Anchor, Container as ContainerIcon, Layers, Truck, ShieldCheck } from "lucide-react";

const DESTINATIONS = [
  { country: "India", ports: ["Nhava Sheva (JNPT)", "Mundra", "Chennai", "Kolkata"] },
  { country: "UAE", ports: ["Jebel Ali", "Khalifa", "Khor Fakkan"] },
  { country: "Vietnam", ports: ["Hai Phong", "Cat Lai", "Cai Mep"] },
  { country: "Bangladesh", ports: ["Chattogram", "Mongla"] },
  { country: "Indonesia", ports: ["Tanjung Priok", "Tanjung Perak"] },
];

const CONTAINERS = [
  { key: "20ft", label: "20ft Standard · ~26 MT" },
  { key: "40ft", label: "40ft Standard · ~28 MT" },
  { key: "40hc", label: "40ft High Cube · ~28 MT" },
];

const fmt = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Math.max(0, Math.round(n || 0)));

const fmt2 = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(Math.max(0, n || 0));

export const CIFCalculator = () => {
  const [factoryPrice, setFactoryPrice] = useState(485); // per MT
  const [country, setCountry] = useState("India");
  const [port, setPort] = useState("Nhava Sheva (JNPT)");
  const [container, setContainer] = useState("20ft");
  const [quantity, setQuantity] = useState(26.5); // MT
  const [freight, setFreight] = useState(1850);
  const [insurance, setInsurance] = useState(180);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const ports = useMemo(
    () => DESTINATIONS.find((d) => d.country === country)?.ports || [],
    [country]
  );

  useEffect(() => {
    if (!ports.includes(port)) setPort(ports[0] || "");
  }, [country, port, ports]);

  const calc = useMemo(() => {
    const goods = (Number(factoryPrice) || 0) * (Number(quantity) || 0);
    const cif = goods + (Number(freight) || 0) + (Number(insurance) || 0);
    const perMt = (Number(quantity) || 0) > 0 ? cif / Number(quantity) : 0;
    return { goods, cif, perMt };
  }, [factoryPrice, quantity, freight, insurance]);

  useEffect(() => {
    setLastUpdated(new Date());
  }, [factoryPrice, country, port, container, quantity, freight, insurance]);

  const timeStr = lastUpdated.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  return (
    <div
      data-testid={CIF.calc}
      className="relative isolate"
    >
      {/* soft halo */}
      <div className="absolute -inset-6 -z-10 bg-gradient-to-br from-blue-200/30 via-transparent to-emerald-200/20 blur-2xl rounded-[2.2rem]" />

      <div className="relative rounded-[1.6rem] bg-white/80 backdrop-blur-2xl border border-white/70 shadow-[0_30px_80px_-20px_rgba(15,23,42,0.18)] ring-1 ring-slate-900/[0.04] overflow-hidden">
        {/* Window chrome */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200/70 bg-white/60">
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-rose-400/80" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-400/80" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/80" />
            <span className="ml-3 text-[11px] tracking-wide text-slate-500 mono">
              demaxtore.app / workspace / estimated-cif
            </span>
          </div>
          <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] tracking-wider uppercase font-semibold text-emerald-700">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 pulse-dot" />
            Live
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5">
          {/* Inputs */}
          <div className="lg:col-span-3 p-6 lg:p-7 border-b lg:border-b-0 lg:border-r border-slate-200/70 space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-slate-400 font-semibold">
                  Estimated CIF Calculation
                </div>
                <div className="font-display text-[20px] font-medium text-slate-900 mt-0.5">
                  Pre-PO Landed Cost
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] tracking-widest uppercase text-slate-400 font-semibold">Ref</div>
                <div className="mono text-[11px] text-slate-600">CIF-{Math.abs((calc.cif | 0)) % 9999}-{country.slice(0,2).toUpperCase()}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field icon={<Package className="h-3.5 w-3.5" />} label="Factory Price (FOB)" suffix="USD / MT">
                <Input
                  data-testid={CIF.calcInputFactoryPrice}
                  type="number"
                  inputMode="decimal"
                  min={0}
                  value={factoryPrice}
                  onChange={(e) => setFactoryPrice(parseFloat(e.target.value || "0"))}
                  className="h-10 mono text-[14px] border-slate-200 focus-visible:ring-blue-500/30"
                />
              </Field>

              <Field icon={<Layers className="h-3.5 w-3.5" />} label="Quantity" suffix="MT">
                <Input
                  data-testid={CIF.calcInputQuantity}
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  min={0}
                  value={quantity}
                  onChange={(e) => setQuantity(parseFloat(e.target.value || "0"))}
                  className="h-10 mono text-[14px] border-slate-200 focus-visible:ring-blue-500/30"
                />
              </Field>

              <Field icon={<MapPin className="h-3.5 w-3.5" />} label="Destination Country">
                <Select value={country} onValueChange={setCountry}>
                  <SelectTrigger data-testid={CIF.calcInputCountry} className="h-10 border-slate-200 focus:ring-blue-500/30">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DESTINATIONS.map((d) => (
                      <SelectItem key={d.country} value={d.country}>{d.country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field icon={<Anchor className="h-3.5 w-3.5" />} label="Destination Port">
                <Select value={port} onValueChange={setPort}>
                  <SelectTrigger data-testid={CIF.calcInputPort} className="h-10 border-slate-200 focus:ring-blue-500/30">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ports.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field icon={<ContainerIcon className="h-3.5 w-3.5" />} label="Container Type">
                <Select value={container} onValueChange={setContainer}>
                  <SelectTrigger data-testid={CIF.calcInputContainer} className="h-10 border-slate-200 focus:ring-blue-500/30">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTAINERS.map((c) => (
                      <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field icon={<Truck className="h-3.5 w-3.5" />} label="Estimated Freight" suffix="USD">
                <Input
                  data-testid={CIF.calcInputFreight}
                  type="number"
                  inputMode="decimal"
                  min={0}
                  value={freight}
                  onChange={(e) => setFreight(parseFloat(e.target.value || "0"))}
                  className="h-10 mono text-[14px] border-slate-200 focus-visible:ring-blue-500/30"
                />
              </Field>

              <Field icon={<ShieldCheck className="h-3.5 w-3.5" />} label="Estimated Insurance" suffix="USD" full>
                <Input
                  data-testid={CIF.calcInputInsurance}
                  type="number"
                  inputMode="decimal"
                  min={0}
                  value={insurance}
                  onChange={(e) => setInsurance(parseFloat(e.target.value || "0"))}
                  className="h-10 mono text-[14px] border-slate-200 focus-visible:ring-blue-500/30"
                />
              </Field>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-slate-100">
              <div className="flex items-center gap-2 text-[11px] text-slate-500">
                <Activity className="h-3.5 w-3.5 text-blue-600" />
                Goods value · <span className="mono text-slate-700">{fmt(calc.goods)}</span>
              </div>
              <div className="text-[11px] text-slate-400 mono">
                Last updated <span data-testid={CIF.calcOutLastUpdated} className="text-slate-600">{timeStr}</span>
              </div>
            </div>
          </div>

          {/* Outputs */}
          <div className="lg:col-span-2 bg-slate-950 text-white p-6 lg:p-7 relative overflow-hidden">
            <div className="absolute inset-0 bg-grid-dark opacity-50 pointer-events-none" />
            <div className="absolute -top-24 -right-20 h-64 w-64 bg-blue-500/20 rounded-full blur-3xl" />

            <div className="relative">
              <div className="text-[10px] tracking-[0.22em] uppercase text-blue-300/80 font-semibold">
                Workspace Output
              </div>
              <div className="font-display text-[18px] font-medium text-white/90 mt-0.5">Estimated CIF</div>

              <div className="mt-5">
                <div className="text-[11px] uppercase tracking-widest text-white/50 font-semibold">Estimated CIF</div>
                <div
                  key={`cif-${calc.cif.toFixed(0)}`}
                  data-testid={CIF.calcOutCif}
                  className="font-display text-4xl lg:text-5xl font-medium tracking-tight mt-1 flip-in"
                >
                  {fmt(calc.cif)}
                </div>
                <div className="mt-1 text-[11px] text-white/40 mono">
                  Factory + Freight + Insurance
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-4">
                <Metric
                  label="Estimated Landed Cost"
                  value={fmt(calc.cif)}
                  testId={CIF.calcOutLanded}
                />
                <Metric
                  label="Estimated Cost / MT"
                  value={fmt2(calc.perMt)}
                  testId={CIF.calcOutPerMt}
                />
              </div>

              <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-2 text-[11px] text-white/60">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-dot" />
                  Ready for Purchase Order
                </div>
                <div className="text-[10px] tracking-widest uppercase text-white/40 mono">
                  {port?.split(" ")[0]} · {container}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, icon, suffix, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <Label className="flex items-center justify-between text-[11px] font-medium text-slate-600 mb-1.5">
      <span className="inline-flex items-center gap-1.5">
        <span className="text-slate-400">{icon}</span>
        {label}
      </span>
      {suffix && <span className="text-[10px] tracking-widest uppercase text-slate-400 mono">{suffix}</span>}
    </Label>
    {children}
  </div>
);

const Metric = ({ label, value, testId }) => (
  <div className="rounded-xl bg-white/[0.04] border border-white/10 p-3">
    <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">{label}</div>
    <div data-testid={testId} className="font-display text-xl font-medium mt-1">{value}</div>
  </div>
);

export default CIFCalculator;
